import { HeaderItem } from "@/components/data-table/types/table.types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DecreeCategory } from "@/types/gql/graphql";
import { ChangeDecreeCategoryModal } from "@/modules/restriction/components/ChangeDecreeCategoryModal";
import { useUpdateDecree } from "@/modules/restriction/hooks/use-decree";
import { usePermanentDecree } from "@/modules/restriction/hooks/use-permanent-decree";
import { useTemporaryDecree } from "@/modules/restriction/hooks/use-temporary-decree";
import { SortOrder } from "@/types/gql/graphql";
import { ArrowIcon, DeleteIcon, EditIcon, PdfIcon } from "components/icons";
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { CreateDecreeInput, Decree, DecreeFilterList, PaginatedDecreeArgs, PaginatedDecrees } from "types/decree.type";
import { useCreateDecreeFile, useRemoveDecree } from "../hooks/use-query-decree-file";
import {
  DecreeAnalyzed,
  DecreeCategoryValues,
  DecreeExtended,
  DecreeFilter,
  DecreeStatus,
  defaultDecreeCells,
  defaultDecreeFilter,
} from "./decree.const";

import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/tooltip";
import { DropdownSelect } from "@/components/form-control/dropdown-select";
import { Checkbox } from "@/components/ui/checkbox";
import useToggle from "@/hooks/use-toggle";
import { cn } from "@/lib/utils";
import RobotEmoji from "assets/robot-emoji-icon.png";
import { TableSkeleton } from "components/data-table";
import { Pagination } from "components/data-table/Pagination";
import { CityContext } from "contexts/city-context-provider";
import { formatDate } from "helper/date-time";
import { isEmpty } from "helper/utils";
import { usePermitDecree } from "../hooks/use-permit-decree";
import { CreateDecreeModal } from "./CreateDecreeModal";
import { DecreeModal } from "./decree-modal";
import { EditDecreeModal } from "./EditDecreeModal";
import { FilterDecree } from "./filter-decree";
import type { Extension, UpdatePermitDecreeInput, UpdateTemporaryDecreeInput } from "@/types/gql/graphql";

interface DecreeListingProps {
  decreeList: PaginatedDecrees;
  selectedDecree?: string;
  onSelectDecree: (input: Decree) => void;
  isLoading: boolean;
  decreesParams: PaginatedDecreeArgs;
  setDecreeParams: Dispatch<SetStateAction<PaginatedDecreeArgs>>;
  refetchDecree: () => void;
}

export const DecreeListing = ({
  decreeList,
  selectedDecree,
  onSelectDecree,
  isLoading,
  decreesParams,
  setDecreeParams,
  refetchDecree,
}: DecreeListingProps) => {
  const [viewFile, setViewFile] = useState<Decree | null>(null);
  const [headerCells, setHeaderCells] = useState<HeaderItem<keyof Decree>[]>(defaultDecreeCells);
  const [filter, setFilter] = useState<DecreeFilter>(defaultDecreeFilter);
  const [decree, setDecree] = useState<Decree | null>(null);
  const [selectedDecreeToChangeCategory, setSelectedDecreeToChangeCategory] = useState<Decree | null>(null);
  const [showCreateDecree, setShowCreateDecree] = useToggle();

  const { currentCity } = useContext(CityContext);
  const { updatePermanentDecreeCategory } = usePermanentDecree(refetchDecree);
  const { updatePermitDecreeCategory } = usePermitDecree(refetchDecree);
  const { updateTemporaryDecreeCategory } = useTemporaryDecree(refetchDecree);
  const { updateDecree } = useUpdateDecree(refetchDecree);
  const { createDecreeFile } = useCreateDecreeFile();
  const { removeDecree } = useRemoveDecree(refetchDecree);
  const [openConfirm, setOpenConfirm] = useToggle();
  const [selectedDecreeId, setSelectedDecreeId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const decreeListSorted = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return decreeList.results;
  }, [isLoading, decreeList]);

  if (!currentCity) {
    return <EmptyData />;
  }

  const changeStatus = async (input: Decree) => {
    if (!input.done) {
      // Allow marking as done only when category exists; Temporary decrees also require type.
      if (isEmpty(input.category)) {
        setErrorMessage("La catégorie de l'arrêté est requise.");
        return;
      }
      if (input.category === DecreeCategory.Temporary && isEmpty(input.type)) {
        setErrorMessage("Le type est requis pour les arrêtés temporaires.");
        return;
      }
    }

    const newDecree = {
      id: input.id,
      done: !input.done,
    };
    await updateDecree(newDecree);
  };

  const handleUpdateDecree = async (
    input: (UpdateTemporaryDecreeInput | UpdatePermitDecreeInput) & { category?: DecreeCategory },
  ) => {
    const newDecree = {
      id: input.id,
      name: input.name,
      done: input.done,
      comments: input.comments,
      title: input.title,
      decreeNumber: input.decreeNumber,
      publishedDate: input.publishedDate,
    };

    switch (input.category) {
      case DecreeCategory.Permit: {
        const permitDecreeInput = input as UpdatePermitDecreeInput;
        await updatePermitDecreeCategory({
          ...newDecree,
          type: permitDecreeInput.type,
          startDate: permitDecreeInput.startDate,
          endDate: permitDecreeInput.endDate,
        });
        break;
      }
      case DecreeCategory.Temporary: {
        const temporaryDecreeInput = input as UpdateTemporaryDecreeInput;
        await updateTemporaryDecreeCategory({
          ...newDecree,
          type: temporaryDecreeInput.type,
          extension: temporaryDecreeInput.extension,
        });
        break;
      }
      case DecreeCategory.Permanent:
        await updatePermanentDecreeCategory(newDecree);
        break;
      default:
        break;
    }
  };

  const handleCreateDecree = async (input: CreateDecreeInput) => {
    await createDecreeFile(input);
    refetchDecree();
  };

  const onClickRemoveDecree = (id: string) => {
    setSelectedDecreeId(id);
    setOpenConfirm();
  };

  const handleRemoveDecree = async () => {
    if (!selectedDecreeId) return;
    await removeDecree({ variables: { id: selectedDecreeId } });
    setSelectedDecreeId(null);
    setOpenConfirm();
  };

  const handleSort = (orderBy?: keyof Decree, direction?: SortOrder) => {
    if (!orderBy || !direction) {
      return;
    }
    const newCells = headerCells.map((cell) => {
      if (cell.orderBy === orderBy) {
        const newDirection = direction === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
        return {
          ...cell,
          sortDirection: newDirection,
        };
      }
      return cell;
    });
    setDecreeParams((prevState) => ({ ...prevState, sortField: orderBy, sortOrder: direction }));
    setHeaderCells(newCells);
  };

  const handleChangePageSize = (value: string) => {
    const newSize = Number(value);
    if (Number.isNaN(newSize)) {
      return;
    }

    setDecreeParams((prevState) => ({
      ...prevState,
      pageSize: newSize,
      pageIndex: 1,
    }));
  };

  const onChangePage = (page: number) => {
    setDecreeParams((prevState) => ({ ...prevState, pageIndex: page }));
  };

  const onChangeFilter = (input: DecreeFilter) => {
    const { searchString, analyzed, status, category, extended } = input;
    setFilter(input);

    const filter = [] as DecreeFilterList;

    if (category.length > 0) {
      filter.push({
        field: "category",
        value: category.join(","),
      });
    }

    if (extended.length === 1) {
      filter.push({
        field: "extension",
        value: String(DecreeExtended.EXTENDED === extended[0]),
      });
    }

    if (status.length === 1) {
      filter.push({
        field: "done",
        value: String(DecreeStatus.DONE === status[0]),
      });
    }

    if (analyzed.length === 1) {
      filter.push({
        field: "analyzed",
        value: String(DecreeAnalyzed.ANALYSED === analyzed[0]),
      });
    }

    if (searchString.length > 0) {
      filter.push({
        field: "text",
        value: searchString,
      });
    }

    setDecreeParams((prevState) => ({
      ...prevState,
      filter,
      pageIndex: 1,
      cityId: currentCity.id,
    }));
  };

  return (
    <>
      <div className="flex flex-col w-full gap-y-2 bg-white p-7">
        <div className="flex flex-col gap-4 mb-11 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-2">
            <span className="font-bold text-lg">Arrêtés municipaux</span>
            <span className="text-sm text-neutral-500">Total : {decreeList.totalCount} arrêtés</span>
            <div className="flex flex-col gap-2">
              <Label>Arrêtés / page</Label>
              <DropdownSelect
                options={["10", "20", "30"]}
                value={String(decreesParams.pageSize)}
                onValueChange={handleChangePageSize}
                placeholder="Arrêtés / page"
                className="w-24 text-xs"
              />
            </div>
          </div>
          <FilterDecree filter={filter} onChange={onChangeFilter} handleCreateDecree={setShowCreateDecree} />
        </div>
        <div className="w-full h-full">
          <Table>
            <TableHeader>
              <TableRow>
                {headerCells.map((item, idx) => (
                  <TableHead className={item.className} key={`decree-table-header-${idx}`}>
                    {item.title}
                    {item.sortable && (
                      <Button variant="ghost" onClick={() => handleSort(item.orderBy, item.sortDirection)}>
                        <ArrowIcon
                          className={cn("size-2", item.sortDirection === SortOrder.Asc ? "-rotate-90" : "rotate-90")}
                        />
                      </Button>
                    )}
                  </TableHead>
                ))}
                <TableHead className="w-20 text-center">Supprimer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="min-h-fit  max-h-full">
              {isLoading ? (
                <TableSkeleton colSpan={12} />
              ) : (
                <>
                  {isEmpty(decreeListSorted) ? (
                    <GenNoRecord
                      colSpan={headerCells.length}
                      message={isLoading ? "Chargement..." : "Aucun résultat trouvé."}
                    />
                  ) : (
                    decreeListSorted.map((item) => (
                      <TableRow
                        className={cn(
                          "hover:none break-words whitespace-normal",
                          selectedDecree === item.id && "bg-neutral-300",
                        )}
                        key={`decree-list-${item.id}`}
                      >
                        <TableCell
                          className="w-auto max-w-[calc(100%-500px)] break-words whitespace-normal text-sm px-6 py-3 cursor-pointer"
                          onClick={() => {
                            if (item.category === DecreeCategory.Temporary) {
                              onSelectDecree(item);
                            }
                          }}
                        >
                          {item.name}
                        </TableCell>
                        <TableCell className="px-6 py-3 text-center w-20">
                          <div className="inline-block">
                            {DecreeCategoryValues[item.category as keyof typeof DecreeCategoryValues]}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 justify-end w-44">
                          {item?.publishedDate ? formatDate(item?.publishedDate) : ""}
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 justify-end w-44">
                          {formatDate(item.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 text-center w-24">
                          {item.category === DecreeCategory.Temporary ? (item.restrictionsCount ?? 0) : "—"}
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 text-center w-40">
                          {item.category === DecreeCategory.Temporary ? renderProlongation(item.extension) : "—"}
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 w-[220px] max-w-[220px]">
                          {item.comments ? (
                            <Tooltip label={item.comments} pos="top" className="max-w-lg whitespace-normal break-words">
                              <span className="block max-w-[170px] truncate">{item.comments}</span>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        <TableCell className="px-6 py-3 text-center w-20">
                          <div className="inline-block">
                            {item.analyzed ? <img className="h-4 w-4" src={RobotEmoji} alt="macaron" /> : ""}
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-3 text-center w-20">
                          <div className="inline-block">
                            <Checkbox checked={item.done} onClick={() => changeStatus(item)} />
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-3 text-center w-20">
                          <div className="inline-block">
                            {item.publicUrl && (
                              <PdfIcon className="h-5 w-5 cursor-pointer" onClick={() => setViewFile(item)} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 text-center w-20" onClick={() => setDecree(item)}>
                          <div className="inline-block">
                            <EditIcon
                              key={`edit-icon-cell-${item.id}`}
                              className="h-5 w-5 cursor-pointer"
                              stroke="black"
                            />
                          </div>
                        </TableCell>
                        <TableCell
                          className="text-sm px-6 py-3 text-center w-20"
                          onClick={() => setSelectedDecreeToChangeCategory(item)}
                        >
                          <div className="inline-block">
                            <EditIcon
                              key={`edit-icon-cell-${item.id}-category`}
                              className="h-5 w-5 cursor-pointer"
                              stroke="black"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-sm px-6 py-3 text-center w-20">
                          <div className="inline-block">
                            <DeleteIcon
                              key={`delete-icon-cell-${item.id}`}
                              className="h-5 w-5 cursor-pointer"
                              onClick={() => onClickRemoveDecree(item.id)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mb-2 flex flex-col items-center justify-end gap-4 md:flex-row">
          <Pagination
            onChangePage={onChangePage}
            currentPage={decreesParams.pageIndex}
            totalPage={decreeList.totalPages}
          />
        </div>
      </div>
      {viewFile && <DecreeModal decree={viewFile} closeModal={() => setViewFile(null)} />}
      {decree && currentCity?.id && (
        <EditDecreeModal decreeData={decree} closeModal={() => setDecree(null)} updateDecreeFile={handleUpdateDecree} />
      )}
      {showCreateDecree && <CreateDecreeModal saveDecreeFile={handleCreateDecree} closeModal={setShowCreateDecree} />}

      {selectedDecreeToChangeCategory && (
        <ChangeDecreeCategoryModal
          decreeData={selectedDecreeToChangeCategory}
          closeModal={() => setSelectedDecreeToChangeCategory(null)}
          refetchDecree={refetchDecree}
        />
      )}
      <AlertDialog open={openConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer un arrêté.</AlertDialogTitle>
            <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer cet arrêté ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={setOpenConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveDecree}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!errorMessage}
        onOpenChange={(open) => {
          if (!open) setErrorMessage(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avertissement concernant la modification de l'arrêté</AlertDialogTitle>
            <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorMessage(null)}>Ok</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const renderProlongation = (extension?: Extension | null) => {
  const originalDecree = extension?.originalDecreeNumber ?? extension?.originalDecreeReference;
  if (!originalDecree) {
    return "Non";
  }

  const label = `Prolonge ${originalDecree}`;
  if (!extension?.originalDecreeReference) {
    return label;
  }

  return (
    <Tooltip label={extension.originalDecreeReference} pos="top" className="max-w-lg whitespace-normal break-words">
      <span className="block truncate">{label}</span>
    </Tooltip>
  );
};

interface GenNoRecordProps {
  colSpan?: number;
  message?: string;
}

export const GenNoRecord = ({ colSpan = 5, message = "No results found" }: GenNoRecordProps) => {
  return (
    <tr className="bg-default">
      <td className="px-6 py-4 whitespace-nowrap bg-white	 text-primary" colSpan={colSpan}>
        <div className="flex items-center justify-center">{message}</div>
      </td>
    </tr>
  );
};

export const EmptyData = () => (
  <div className="flex flex-col w-full gap-y-2 bg-white p-7">
    <div className="flex justify-between mb-11">
      <span className="font-bold text-lg">Arrêtés municipaux</span>
    </div>
    <div className="w-full bottom-2 pb-8 h-full">
      <Table>
        <TableHeader>
          <TableRow>
            {defaultDecreeCells.map((item, idx) => (
              <TableHead className={item.className} key={`decree-skeleton-header-${idx}`}>
                {item.title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className="min-h-fit  max-h-full">
          <GenNoRecord message="Please select a city" colSpan={6} />
        </TableBody>
      </Table>
    </div>
  </div>
);
